import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaExternalLinkAlt, FaStore } from 'react-icons/fa';
import ApiImage from '../../../../components/misc/ApiImage';
import Button from '../../../../components/misc/Button';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import sponsorService from '../../../../services/sponsorService';
import Sponsor from '../../../../types/sponsor';

type LocationState = {
  sponsor?: Sponsor;
};

function formatWebsiteHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
}

export default function SponsorOverviewPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sponsorId } = useParams<{ sponsorId: string }>();
  const location = useLocation();
  const community = useSelectedCommunity();
  const stateSponsor = (location.state as LocationState | null)?.sponsor;

  const [sponsor, setSponsor] = useState<Sponsor | null>(
    stateSponsor && String(stateSponsor.sponsorId) === sponsorId ? stateSponsor : null,
  );
  const [loading, setLoading] = useState(!sponsor);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fromState = stateSponsor && String(stateSponsor.sponsorId) === sponsorId ? stateSponsor : null;

    if (fromState) {
      setSponsor(fromState);
      setLoading(false);
      setNotFound(false);
      return undefined;
    }

    setSponsor(null);
    setNotFound(false);

    if (!community?.id || !sponsorId) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    sponsorService
      .getCommunitySponsors(community.id)
      .then((sponsors) => {
        if (cancelled) return;
        const match = sponsors.find((s) => String(s.sponsorId) === sponsorId) ?? null;
        if (match) {
          setSponsor(match);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [community?.id, sponsorId, stateSponsor]);

  const title = useMemo(() => {
    if (!sponsor) return '';
    return sponsor.name;
  }, [sponsor]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/community');
    }
  };

  const handleOpenWebsite = () => {
    if (!sponsor?.url || !community?.id) return;
    sponsorService.trackWebsiteOpen(community.id, sponsor.sponsorId).catch(() => {
      /* fire-and-forget */
    });
    window.open(sponsor.url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-sm text-details/70">{t('page.sponsors.overview.loading')}</p>
      </div>
    );
  }

  if (notFound || !sponsor) {
    return (
      <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 px-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary-200">
          <FaStore className="h-7 w-7 text-primary/40" />
        </div>
        <h1 className="text-xl font-semibold text-primary">{t('page.sponsors.overview.notFound')}</h1>
        <p className="text-sm text-details/70">{t('page.sponsors.overview.notFoundHint')}</p>
        <Button variant="primary" onClick={() => navigate('/community')}>
          {t('page.sponsors.overview.backToCommunity')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 pb-10">
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex w-fit items-center gap-2 text-sm font-medium text-primary transition-opacity hover:opacity-70">
        <FaArrowLeft className="h-3.5 w-3.5" />
        {t('page.sponsors.overview.back')}
      </button>

      <div className="overflow-hidden rounded-2xl border border-secondary-50 bg-secondary">
        <div className="aspect-[16/9] w-full overflow-hidden bg-secondary-200 sm:aspect-[2/1]">
          {sponsor.logo ? (
            <ApiImage src={sponsor.logo} alt={title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <FaStore className="h-14 w-14 text-primary/35" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-5 p-5 sm:p-6">
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary/50">
              {t('page.community.sponsors')}
            </p>
            <h1 className="text-2xl font-bold leading-tight text-primary sm:text-3xl">{title}</h1>
          </div>

          {!!sponsor.description && (
            <div className="flex flex-col gap-2 border-t border-secondary-50 pt-5">
              <h2 className="text-sm font-semibold text-primary">{t('page.sponsors.overview.about')}</h2>
              <p className="whitespace-pre-wrap text-base leading-relaxed text-details/80">{sponsor.description}</p>
            </div>
          )}

          {!sponsor.description && !sponsor.url && (
            <p className="border-t border-secondary-50 pt-5 text-sm text-details/70">
              {t('page.sponsors.overview.noDetails')}
            </p>
          )}

          {!!sponsor.url && (
            <div className="flex flex-col gap-2 border-t border-secondary-50 pt-5">
              <Button
                variant="primary"
                onClick={handleOpenWebsite}
                className="inline-flex w-full items-center justify-center gap-2 py-3 text-base">
                <FaExternalLinkAlt className="h-3.5 w-3.5" />
                {t('page.sponsors.overview.openWebsite')}
              </Button>
              <p className="text-center text-xs text-details/60">
                {t('page.sponsors.overview.opensExternal', {
                  host: formatWebsiteHost(sponsor.url),
                })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
